// Raporlar's tab strip: Genel durum · Güçlü ve zayıf yönler · Ödevler · Sınavlar — four VIEWS, each
// reading a fixed set of kinds and narrowing it with the Kaynak row (LearnerSourceDefs).
//
// Ödevler and Sınavlar used to be GROUPS holding one view per kind behind a type filter (Şube ödevi /
// Bireysel ödev; Yazılı / Deneme / Optik). The holistic report shows the kinds of a group TOGETHER, so
// the kind filter is gone and `?tab=` names the view. Every link written before — a kind id, or the
// pre-rename `CERTIFICATE_EXAM` — still opens the view that now holds that kind.

import {
  STRONG_WEAK_SOURCE_IDS,
  retrieveLearnerSourceIdsByGroup,
} from "./LearnerSourceDefs";

export type ReportViewId =
  | "GENERAL_STATUS"
  | "STRONG_WEAK"
  | "ASSIGNMENTS"
  | "EXAMS";

export const REPORT_VIEW_ID_LIST: ReportViewId[] = [
  "GENERAL_STATUS",
  "STRONG_WEAK",
  "ASSIGNMENTS",
  "EXAMS",
];

// A kind id (the old per-kind tabs) or a pre-rename id → the view that holds it now.
const LEGACY_REPORT_TAB_ID_ALIAS: Record<string, ReportViewId> = {
  BRANCH_ASSIGNMENT: "ASSIGNMENTS",
  SUPPORT_ASSIGNMENT: "ASSIGNMENTS",
  PERSONALIZED_ASSIGNMENT: "ASSIGNMENTS",
  HANDWRITTEN_EXAM: "EXAMS",
  MOCK_EXAM: "EXAMS",
  // The deneme tab was `CERTIFICATE_EXAM` before the MockExam rename.
  CERTIFICATE_EXAM: "EXAMS",
  // Optical exams are retired; an old link lands on Sınavlar rather than on nothing.
  OPTICAL_EXAM: "EXAMS",
};

/** The `?tab=` value as a current view id: a legacy id maps to its view, anything else is kept. */
export const normalizeReportTabId = (tabId: string | null): string | null =>
  tabId === null ? null : (LEGACY_REPORT_TAB_ID_ALIAS[tabId] ?? tabId);

/** True when the `?tab=` value is a legacy id the address should be rewritten from. */
export const isLegacyReportTabId = (tabId: string | null): boolean =>
  tabId !== null && tabId in LEGACY_REPORT_TAB_ID_ALIAS;

/**
 * The Kaynak scope of a view — the kinds it actually reads. `undefined` means every kind (Genel
 * durum). A view's chip row offers only these, and its lock is computed within them.
 */
export const retrieveReportViewSourceIds = (
  viewId: string,
): string[] | undefined => {
  switch (viewId) {
    case "STRONG_WEAK":
      return STRONG_WEAK_SOURCE_IDS;
    case "ASSIGNMENTS":
      return retrieveLearnerSourceIdsByGroup("assignment");
    case "EXAMS":
      return retrieveLearnerSourceIdsByGroup("exam");
    default:
      return undefined;
  }
};

/**
 * The views the strip offers, in strip order, given which kinds the learner has evidence in.
 *
 * - Genel durum: any kind at all.
 * - Güçlü ve zayıf yönler: any time-bearing ödev kind with SCORED answers (`scoredSourceIds`).
 * - Ödevler: any ödev kind with a row to list (`listableSourceIds`) — a destek whose results are not
 *   published yet is a row (it says so) even though it carries no score.
 * - Sınavlar: any exam kind with a row to list.
 */
export const buildReportViewIdList = (
  listableSourceIds: string[],
  scoredSourceIds: string[],
): ReportViewId[] => {
  const listable = listableSourceIds || [];
  const scored = scoredSourceIds || [];
  const hasAny = (ids: string[], scope: string[]) =>
    ids.some((id) => scope.includes(id));
  const viewIdList: ReportViewId[] = [];
  if (listable.length > 0 || scored.length > 0) viewIdList.push("GENERAL_STATUS");
  if (hasAny(scored, STRONG_WEAK_SOURCE_IDS)) viewIdList.push("STRONG_WEAK");
  if (hasAny(listable, retrieveLearnerSourceIdsByGroup("assignment")))
    viewIdList.push("ASSIGNMENTS");
  if (hasAny(listable, retrieveLearnerSourceIdsByGroup("exam")))
    viewIdList.push("EXAMS");
  return viewIdList;
};
